import { PROCESS_SAMPLE_DURATION_SECONDS } from '@electron/shared';

import { isDev, isProd } from '@/env';
import { FEATURES } from '@/features/feature-flags';
// Deep import: the `@/features/shortcuts` barrel pulls in hooks and stores
import {
  SHORTCUT_DEFINITIONS,
  type ShortcutId,
} from '@/features/shortcuts/shortcuts.registry';
import { AppCapability, inElectron, isMac } from '@/utils/capabilities';

import { type SettingsMenuContext } from './settings-menu.config';
import { shortcutSearchId } from './settings-search-dom';
import {
  archivedChatsSearchProvider,
  mcpClientsSearchProvider,
} from './settings-search.providers';

export interface SettingsSearchEntry {
  /** Unique across the manifest; rows tag themselves via `SettingsSection.Item searchId` */
  id: string;
  /** Section key — the part after `settings/` in `settingsMenuConfig` hashes */
  sectionId: string;
  label: string;
  description?: string;
  keywords?: string[];
  /** Row-level gating mirroring the pane's render logic; section-level gating lives in `settingsMenuConfig` */
  enabled?: (ctx: SettingsMenuContext) => boolean;
}

/** Search source for panes whose rows are dynamic or server-driven, resolved at search time */
export interface SettingsSearchProvider {
  sectionId: string;
  getEntries(ctx: SettingsMenuContext): Promise<SettingsSearchEntry[]>;
}

const forSection = (
  sectionId: string,
  entries: Omit<SettingsSearchEntry, 'sectionId'>[]
): SettingsSearchEntry[] => entries.map((entry) => ({ ...entry, sectionId }));

const modKey = isMac() ? '⌘' : 'Ctrl';

export const settingsSearchEntries: SettingsSearchEntry[] = [
  ...forSection('KeyboardShortcuts', [
    {
      id: 'shortcuts.meetings.widget',
      label: 'Expand/collapse meeting widget',
      keywords: ['shortcut', 'hotkey', 'floating', 'system-wide'],
      enabled: () => inElectron(),
    },
  ]),
  ...forSection('Proactivity', [
    {
      id: 'proactivity.importance',
      label: 'Minimum card importance',
      description: 'Hides cards rated less important than this',
      keywords: ['cards', 'importance', 'home', 'filter'],
    },
    {
      id: 'proactivity.borderline-cards',
      label: 'Borderline cards',
      description:
        "How readily your proactive agent's routines create cards for things that may or may not matter to you",
      keywords: ['cards', 'more', 'fewer', 'proactive', 'routines'],
    },
    {
      id: 'proactivity.card-delivery',
      label: 'Send cards to a messaging channel',
      description: 'Sends important cards to iMessage or WhatsApp',
      keywords: ['cards', 'channel', 'imessage', 'whatsapp', 'delivery'],
      enabled: ({ hasFeature }) =>
        hasFeature(FEATURES.PROACTIVE_CARDS_IN_CHANNELS),
    },
  ]),
  ...forSection('AutoReview', [
    {
      id: 'auto-review.rules',
      label: 'Auto-review rules',
      description:
        'Choose which actions run without asking and which ask you first',
      keywords: ['rules', 'approval', 'ask first', 'auto run', 'allow'],
    },
  ]),
  ...forSection('ProactiveAgent', [
    {
      id: 'proactive-agent.view',
      label: 'Internal proactive agent view',
      description:
        'When off, this device shows what users who are not internal see, without proactive agent chats or routines',
      keywords: ['home', 'feed', 'chats', 'routines', 'not internal', 'toggle'],
    },
    {
      id: 'proactive-agent.run',
      label: 'Run proactive agents',
      description: 'Run an agent now, pause it, or delete its chat',
      keywords: [
        'run now',
        'pause',
        'resume',
        'create agent',
        'delete chat',
        'delete agent',
        'routines',
        'compare',
      ],
    },
  ]),
  ...forSection('General', [
    {
      id: 'general.name',
      label: 'What should Littlebird call you?',
      keywords: ['name'],
    },
    {
      id: 'general.aliases',
      label: 'Aliases',
      description:
        'Include your nicknames, online handles, and other identifiers, separated by commas',
    },
    {
      id: 'general.email',
      label: 'Email',
      description: 'Change your email address',
    },
    {
      id: 'general.timezone',
      label: 'Current timezone',
      keywords: ['timezone'],
    },
    {
      id: 'general.device-timezone',
      label: 'Use this device to set my timezone',
      description:
        "Turn this off on devices that travel or aren't where you live, so they don't change your timezone.",
    },
    {
      id: 'general.share-usage-data',
      label: 'Share usage data',
      description: 'Help improve Littlebird by sharing usage data',
      enabled: ({ hasFeature }) => hasFeature(FEATURES.DATA_SHARING),
    },
    {
      id: 'general.app-version',
      label: 'Littlebird version',
      keywords: ['update', 'version', 'channel'],
      enabled: ({ isCapable }) => isCapable(AppCapability.DesktopExclusive),
    },
    {
      id: 'general.sign-out',
      label: 'Sign Out',
      description: 'Sign out from Littlebird',
      keywords: ['logout'],
    },
  ]),
  ...forSection('System', [
    {
      id: 'system.launch-on-startup',
      label: 'Launch Littlebird on startup',
      description:
        'Automatically start Littlebird when you log in to your computer',
    },
    {
      id: 'system.notifications',
      label: 'Notifications',
      description: 'Show Littlebird notifications',
    },
    {
      id: 'system.notification-sound',
      label: 'Notification Sound',
      description:
        'Play a sound for notifications like meeting reminders and more',
    },
    {
      id: 'system.notification-sound-choice',
      label: 'Alert Sound',
      description: 'Which sound plays for alerts like meeting reminders',
      keywords: ['notification sound', 'chime', 'chirp', 'system sound'],
      enabled: () => isMac(),
    },
    {
      id: 'system.time-format',
      label: 'Time Format',
      description: 'How times are displayed throughout the app',
    },
    {
      id: 'system.show-app-in',
      label: 'Show App In',
      description: 'Control the visibility of the app when closed',
      keywords: ['dock', 'status bar'],
    },
  ]),
  ...forSection('Appearance', [
    {
      id: 'appearance.color-mode',
      label: 'Color Mode',
      keywords: ['theme', 'dark', 'light'],
    },
    {
      id: 'appearance.theme',
      label: 'Theme',
      keywords: ['sidebar', 'color', 'preset'],
    },
    {
      id: 'appearance.rail-labels',
      label: 'Show Labels in Icon Sidebar',
      description: 'Show a label under each icon in the icon sidebar',
      keywords: ['sidebar', 'rail', 'labels', 'icons', 'names'],
    },
    {
      id: 'appearance.sidebar-whats-new',
      label: "Show What's New in Sidebar",
      description: 'Show new changelog entries at the bottom of the sidebar',
      keywords: ['sidebar', 'changelog', 'release notes', 'updates'],
    },
    {
      id: 'appearance.accent-buttons',
      label: 'Accent Buttons',
      description: 'Use the theme color for primary buttons',
      keywords: ['accent', 'primary'],
    },
    {
      id: 'appearance.extra-wide-chat',
      label: 'Extra Wide Chat',
      description: 'Choose whether to make the chat extra wide',
    },
    {
      id: 'appearance.hide-internal-tools',
      label: 'Hide Internal Tools',
      description:
        'Hide internal-only menus, modes and debug views, as customers see the app',
      keywords: ['demo', 'demo mode', 'preview as user', 'customer view'],
      enabled: ({ hasInternalAccess }) => hasInternalAccess,
    },
    {
      id: 'appearance.font-size',
      label: 'Font Size',
      description: 'Adjust the size of text across the app',
    },
    {
      id: 'appearance.dyslexic-font',
      label: 'Dyslexia-Friendly Font',
      description:
        'Use OpenDyslexic, a typeface designed for readers with dyslexia',
      keywords: ['typeface', 'accessibility', 'readability'],
    },
    {
      id: 'appearance.code-block-wrapping',
      label: 'Code Block Wrapping',
      description: 'Enable or disable code block wrapping',
    },
    {
      id: 'appearance.close-idle-tabs',
      label: 'Close Idle Tabs',
      description: "Close tabs you haven't opened in a while",
      keywords: ['tabs', 'auto close', 'inactive', 'unused', 'clean up'],
      enabled: () => inElectron(),
    },
  ]),
  ...forSection('Chat', [
    {
      id: 'chat.custom-instructions',
      label: 'Custom Instructions',
      description:
        'Personalize your interactions with Littlebird by providing your own instructions',
    },
    {
      id: 'chat.assistant-notes',
      label: 'Assistant Notes',
      description:
        'Review and edit what Littlebird has remembered from past chats to guide future conversations',
      keywords: ['memory'],
    },
    {
      id: 'chat.vfs-assistant-notes',
      label: 'VFS extension of Assistant Notes',
      description:
        "Browse extended memory files stored in Littlebird's virtual filesystem",
      enabled: ({ isInternalUser }) => isInternalUser,
    },
    {
      id: 'chat.read-only-memory',
      label: 'Read-Only Memory',
      description:
        'Knowledge and insights that Littlebird has learned from previous conversations',
      // The pane gates this row on its `echoNotesEnabled` alias, which is
      // currently `userStore.isInternalUser` — keep in sync with `settings-chat.tsx`
      enabled: ({ isInternalUser }) => isInternalUser,
    },
    {
      id: 'chat.require-mod-enter-to-send',
      label: `Require ${modKey}+Enter to Send`,
      description: `When enabled, pressing Enter adds a new line and ${modKey}+Enter sends the message`,
    },
    {
      id: 'chat.open-external-links',
      label: 'Open external links without confirmation',
      description:
        'Unsafe — Skips the warning before opening unknown potentially malicious links',
    },
    {
      id: 'chat.show-thoughts',
      label: 'Show Thoughts',
      description: "Show Littlebird's reasoning in the chat",
    },
    {
      id: 'chat.previous-turn-preview',
      label: 'Show Previous-Turn Preview',
      description:
        'Keep part of the previous turn visible while Littlebird responds',
    },
    {
      id: 'chat.past-screen-activity',
      label: 'Disable screen context retrieval',
      description:
        'Do not use information previously captured from your screen when answering',
      enabled: ({ isInternalUser }) => isInternalUser,
    },
    {
      id: 'chat.project-suggestions',
      label: 'Project Suggestions',
      description: 'Suggest an existing or new project for a chat',
    },
    {
      id: 'chat.email-attribution',
      label: 'Add “Sent via Littlebird” to emails',
      description:
        'Append a short Littlebird link to emails sent on your behalf',
      keywords: ['email', 'footer', 'signature', 'attribution', 'gmail'],
      enabled: ({ hasFeature }) => hasFeature(FEATURES.EMAIL_ATTRIBUTION),
    },
    {
      id: 'chat.cost-footer',
      label: 'Cost Footer (internal only)',
      description: 'Show token + USD breakdown under the composer',
      enabled: ({ isInternalUser }) => isInternalUser,
    },
  ]),
  ...forSection('Meetings', [
    {
      id: 'meetings.prep-instructions',
      label: 'Meeting Prep',
      description:
        'Personalize how Littlebird prepares you for upcoming meetings',
      keywords: ['custom instructions', 'meeting prep'],
    },
    {
      id: 'meetings.summary-instructions',
      label: 'Meeting Summary',
      description:
        'Personalize how Littlebird summarizes your recorded meetings',
      keywords: ['custom instructions', 'meeting summary'],
    },
    {
      id: 'meetings.assist',
      label: 'Turn on Meeting Assist',
      description: 'Answer questions and draft follow-ups as your meeting runs',
      keywords: ['meeting assist', 'suggestions', 'cards', 'turn off'],
      enabled: ({ hasFeature }) => hasFeature(FEATURES.MEETING_ASSIST),
    },
    {
      id: 'meetings.assist-popups',
      label: 'Meeting Assist popups',
      description: 'Pop new cards up beside the collapsed meeting widget',
      keywords: ['meeting assist', 'popup', 'notification', 'cards'],
      enabled: ({ hasFeature }) =>
        hasFeature(FEATURES.MEETING_ASSIST) &&
        hasFeature(FEATURES.MEETING_ASSIST_POPUPS),
    },
    {
      id: 'meetings.assist-instructions',
      label: 'Meeting Assist instructions',
      description:
        'Tell Littlebird what to suggest during your meetings, and what to leave out',
      keywords: [
        'custom instructions',
        'meeting assist',
        'suggestions',
        'cards',
      ],
      enabled: ({ hasFeature }) => hasFeature(FEATURES.MEETING_ASSIST),
    },
    {
      id: 'meetings.intelligence',
      label: 'Meeting Intelligence',
      description:
        'Automatically catch action items and follow-ups as they come up in your meetings',
      keywords: ['meeting intelligence', 'intent', 'drafts', 'turn off'],
      enabled: ({ hasFeature }) =>
        hasFeature(FEATURES.MEETING_INTENT_DETECTION),
    },
    {
      id: 'meetings.automatic-prep',
      label: 'Prepare for meetings automatically',
      description: 'Generate a prep brief before each meeting',
    },
    {
      id: 'meetings.prep-lead-time',
      label: 'Prepare meetings ahead by',
      description: 'How early the prep brief is generated',
      keywords: ['lead time', 'meeting prep', 'email'],
    },
    {
      id: 'meetings.prep-email',
      label: 'Email me meeting prep',
      description: "Send the prep brief to your inbox when it's ready",
    },
    {
      id: 'meetings.summary-email',
      label: 'Email me meeting summaries',
      description: 'Send the summary to your inbox after each recorded meeting',
    },
    {
      id: 'meetings.spoken-language',
      label: 'Spoken language',
      description: "Language used to transcribe what's said in meetings",
      enabled: ({ isCapable }) => isCapable(AppCapability.RecordMeeting),
    },
    {
      id: 'meetings.transcription-engine',
      label: 'Preferred Transcription Engine',
      description: 'Choose where speech recognition runs for meetings',
      enabled: ({ isCapable, hasFeature }) =>
        isCapable(AppCapability.RecordMeeting) &&
        hasFeature(FEATURES.LOCAL_TRANSCRIPTION_ENGINE_PICKER) &&
        hasFeature(FEATURES.LOCAL_TRANSCRIPTION) &&
        isMac(),
    },
    {
      id: 'meetings.microphone',
      label: 'Microphone',
      description: 'Pin a mic for meetings, or follow the system default',
      enabled: ({ isCapable }) => isCapable(AppCapability.RecordMeeting),
    },
    {
      id: 'meetings.custom-vocabulary',
      label: 'Custom vocabulary',
      description:
        'Littlebird uses these when transcribing. Add company names, project names, or other terms. Comma-separate words.',
      enabled: ({ isCapable, hasFeature }) =>
        isCapable(AppCapability.RecordMeeting) &&
        hasFeature(FEATURES.MEETING_NOTES_CUSTOM_VOCABULARY),
    },
    {
      id: 'meetings.detection-mode',
      label: 'When a meeting app is detected',
      description: 'What Littlebird does the moment it spots a meeting',
      enabled: ({ isCapable }) => isCapable(AppCapability.RecordMeeting),
    },
    {
      id: 'meetings.auto-stop',
      label: 'Stop recording after inactivity',
      description: 'End the recording once a meeting goes quiet',
      enabled: ({ isCapable }) => isCapable(AppCapability.RecordMeeting),
    },
    {
      id: 'meetings.show-summary-after-auto-stop',
      label: 'Show summary when a meeting ends',
      description:
        'Open the summary when a meeting ends, without interrupting your typing',
      enabled: ({ isCapable }) => isCapable(AppCapability.RecordMeeting),
    },
    {
      id: 'meetings.excluded-apps',
      label: 'Excluded meeting apps',
      description: "Littlebird won't detect meetings in these apps",
      enabled: ({ isCapable }) => isCapable(AppCapability.RecordMeeting),
    },
    {
      id: 'meetings.reminder',
      label: 'Remind me before meetings',
      description: 'How early to nudge you before a meeting starts',
      enabled: ({ isCapable }) => isCapable(AppCapability.DesktopExclusive),
    },
    {
      id: 'meetings.notification-filter',
      label: 'Notify me about',
      description: 'Which upcoming meetings trigger a notification',
      enabled: ({ isCapable }) => isCapable(AppCapability.DesktopExclusive),
    },
    {
      id: 'meetings.ignore-solo-events',
      label: 'Ignore solo events',
      description:
        'Leave events with no other guests out of meetings, prep, and notifications',
    },
    {
      id: 'meetings.calendar-look-ahead',
      label: 'Calendar Look-Ahead Window',
      description: 'How far in advance to fetch upcoming calendar events',
    },
    {
      id: 'meetings.calendar-max-events',
      label: 'Max Calendar Events',
      description:
        'Maximum calendar events to fetch within the look-ahead window',
    },
    {
      id: 'meetings.excluded-event-types',
      label: 'Excluded Event Types',
      description:
        'Calendar event types to hide from meeting notes, the menu bar, and Home',
    },
    {
      id: 'meetings.excluded-statuses',
      label: 'Excluded Statuses',
      description:
        'Calendar event statuses to hide from meeting notes, the menu bar, and Home',
    },
    {
      id: 'meetings.status-window',
      label: 'Floating Meeting Widget',
      description: 'Show the meeting widget always or only during meetings',
      enabled: ({ isCapable }) =>
        isCapable(AppCapability.RecordMeeting) && isDev,
    },
    {
      id: 'meetings.hide-widget-screen-share',
      label: 'Hide Widget From Screen Sharing',
      description:
        'Keep the floating meeting widget invisible to screen capture and shared screens',
      enabled: ({ isCapable }) => isCapable(AppCapability.RecordMeeting),
    },
    {
      id: 'meetings.menubar-calendar',
      label: 'Menu bar calendar',
      description: 'Show upcoming calendar events in the macOS menu bar',
      enabled: ({ isCapable }) => isCapable(AppCapability.DesktopExclusive),
    },
    {
      id: 'meetings.menubar-include-events',
      label: 'Show events from',
      description: 'How far ahead the menu bar calendar lists events',
      enabled: ({ isCapable }) => isCapable(AppCapability.DesktopExclusive),
    },
    {
      id: 'meetings.menubar-preview-lead-time',
      label: 'Preview the next event',
      description: 'When the next event appears in the menu bar chip',
      enabled: ({ isCapable }) => isCapable(AppCapability.DesktopExclusive),
    },
  ]),
  ...forSection('Hummingbird', [
    {
      id: 'hummingbird.enable',
      label: 'Enable Hover',
      description:
        "Open Littlebird from anywhere and ask about what's on your screen.",
    },
    {
      id: 'hummingbird.global-shortcut',
      label: 'Global Shortcut',
      description: 'Choose the global shortcut used to open Hover',
      enabled: ({ hummingbirdEnabled }) => hummingbirdEnabled,
    },
    {
      id: 'hummingbird.voice-shortcut',
      label: 'Voice Shortcut',
      description: 'Speak to Littlebird from anywhere on your desktop.',
      enabled: ({ hummingbirdEnabled }) => hummingbirdEnabled,
    },
    {
      id: 'hummingbird.auto-send-voice',
      label: 'Auto-Send Voice Messages',
      description:
        'Send your message as soon as the transcription finishes. Turn off to review the transcript before sending.',
      enabled: ({ hummingbirdEnabled }) => hummingbirdEnabled,
    },
    {
      id: 'hummingbird.always-new-chat',
      label: 'Always Start New Chat',
      description: 'Start with a fresh chat each time you open Hover',
      enabled: ({ hummingbirdEnabled }) => hummingbirdEnabled,
    },
    {
      id: 'hummingbird.front-app-context',
      label: 'Include Front App Context',
      description:
        'Include context from the frontmost app when Hover opens to a new chat.',
      enabled: ({ hummingbirdEnabled }) => hummingbirdEnabled,
    },
    {
      id: 'hummingbird.hide-screen-sharing',
      label: 'Hide From Screen Sharing',
      description: 'Keep Hover invisible to screen capture and shared screens',
      enabled: ({ hummingbirdEnabled }) => hummingbirdEnabled,
    },
  ]),
  ...forSection('DataControls', [
    {
      id: 'data-controls.screenshot-previews',
      label: 'Keep Previews of Phone Screenshots',
      description: 'Only you can see them.',
      keywords: ['screenshot', 'phone', 'preview', 'image'],
      enabled: ({ backendChoice }) => backendChoice === 'typescript',
    },
    {
      id: 'data-controls.delete-last-hour',
      label: 'Delete Last Hour of Context',
      description: 'Remove all context collected in the last hour',
    },
    {
      id: 'data-controls.delete-last-day',
      label: 'Delete Last Day of Context',
      description: 'Remove all context collected in the last 24 hours',
    },
    {
      id: 'data-controls.delete-custom-range',
      label: 'Delete Context for a Time Period',
      description:
        'Remove a recent stretch or specific dates, e.g. all of March',
    },
    {
      id: 'data-controls.delete-all-context',
      label: 'Delete All Context',
      description:
        'Permanently remove all context collected. This action cannot be undone.',
    },
    {
      id: 'data-controls.hipaa',
      label: 'Enable HIPAA',
      keywords: ['baa', 'compliance', 'privacy mode'],
    },
    {
      id: 'data-controls.delete-account',
      label: 'Delete Your Account',
      description: 'Permanently delete your account and all associated data',
    },
    {
      id: 'data-controls.reset-onboarding',
      label: 'Reset Onboarding',
      description:
        'Clear onboarding progress to test new onboarding experiences',
      enabled: ({ isInternalUser }) => isInternalUser,
    },
    {
      id: 'data-controls.singular-device-id',
      label: 'Singular Device ID',
      enabled: ({ isInternalUser }) => isInternalUser && !isProd,
    },
  ]),
  ...forSection('Journal', [
    {
      id: 'journal.expand-sections',
      label: 'Expand Sections by Default',
      description:
        'Enable or disable the expansion of journal sections by default',
    },
    {
      id: 'journal.email-delivery-time',
      label: 'Email Delivery Time',
      description:
        'Choose the time of day you want to receive your journal notification',
    },
    {
      id: 'journal.email-content',
      label: 'Email Content',
      description: 'Choose between receiving a full journal or a TLDR summary',
    },
    {
      id: 'journal.language',
      label: 'Language',
      description: 'Choose the language for your journal',
    },
    {
      id: 'journal.progress-section',
      label: "Show Past Week's Progress",
      description:
        'Control when to include a progress summary from the past week',
    },
    {
      id: 'journal.mobile-push',
      label: 'Mobile Push Notifications',
      description:
        'Receive push notifications on your mobile device when your journal is ready',
    },
    {
      id: 'journal.reset-configuration',
      label: 'Reset Journal Configuration',
    },
  ]),
  ...forSection('VoiceMode', [
    {
      id: 'voice-mode.speech-features',
      label: 'Speech Features',
      description: 'Show response read-aloud and voice controls',
    },
    {
      id: 'voice-mode.assistant-voice',
      label: 'Assistant Voice',
      description: 'Voice used for read-aloud and spoken responses',
    },
    {
      id: 'voice-mode.thinking-sound',
      label: 'Thinking Sound',
      description:
        'Play a soft bird chirp now and then while the assistant thinks or uses tools',
    },
    {
      id: 'voice-mode.pause-length',
      label: 'Pause Length (seconds)',
      description: 'Seconds of silence before what you said submits',
    },
    {
      id: 'voice-mode.submission-sound-delay',
      label: 'Submission Sound Delay (seconds)',
      description:
        'Maximum wait for transcription before playing the confirmation sound',
    },
    {
      id: 'voice-mode.max-recording-time',
      label: 'Max Recording Time (seconds)',
      description: 'Maximum length of your Voice Mode messages',
    },
    {
      id: 'voice-mode.speech-volume-threshold',
      label: 'Speech Volume Threshold (RMS)',
      description: 'Raise this to ignore more background sound',
    },
    {
      id: 'voice-mode.speech-frequency-range',
      label: 'Speech Frequency Range',
      description: 'Frequencies whose energy counts toward the speech score',
    },
    {
      id: 'voice-mode.speech-frequency-threshold',
      label: 'Speech-Frequency Threshold (%)',
      description: 'Raise this to reject more sound outside speech frequencies',
    },
  ]),
  ...forSection('Support', [
    {
      id: 'support.email-support',
      label: 'Email Support',
      description: 'Contact us at support@littlebird.ai',
    },
    {
      id: 'support.discord',
      label: 'Join our Discord',
      description:
        'Join our Discord server for real time support and discussions',
    },
    {
      id: 'support.report-performance',
      label: 'Report Performance Issues',
      description: `Experiencing slowdowns or high resource usage? Create a ${PROCESS_SAMPLE_DURATION_SECONDS}-second diagnostic snapshot to help us troubleshoot the issue.`,
    },
    {
      id: 'support.sync-diagnostics',
      label: 'Sync diagnostics',
      description:
        'Where this device is relative to the server, and what is still waiting to upload.',
    },
  ]),
];

const shortcutEntryEnabled = (id: ShortcutId): boolean => {
  if (id.startsWith('tabs.')) return inElectron();
  if (id.startsWith('hummingbird.')) return isMac();
  return true;
};

export const keyboardShortcutsSearchProvider: SettingsSearchProvider = {
  sectionId: 'KeyboardShortcuts',
  getEntries: async () =>
    (Object.keys(SHORTCUT_DEFINITIONS) as ShortcutId[])
      .filter(shortcutEntryEnabled)
      .map((id) => ({
        id: shortcutSearchId(id),
        sectionId: 'KeyboardShortcuts',
        label: SHORTCUT_DEFINITIONS[id].label,
        keywords: ['shortcut', 'hotkey'],
      })),
};

/** Providers for panes with dynamic content; Team is skipped (its `item` param selects a tab) */
export const settingsSearchProviders: SettingsSearchProvider[] = [
  keyboardShortcutsSearchProvider,
  mcpClientsSearchProvider,
  archivedChatsSearchProvider,
];
